import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import UserNotification from "util/UserNotification";
import { defaultCompare } from "logic/DefaultCompare";
import { defaultOnError } from "util/conditional/onError";
import {
  bulkSigmaRepoDelete,
  fetchStreams,
  fetchNotifications,
  listGitRepos,
  addGitRepo,
  updateGitRepo,
  deleteSigmaRepo,
  listRepoContent,
  addDefaultRepo,
  importAllRulesFromRepo,
  refreshRepoRules,
  validateSigmaRuleZip,
  validateSigmaRuleYaml,
  bulkImportFromRepo,
  bulkUploadEventDefs
} from "./api/sigmaAPI";
const sigmaRulesKey = "get-sigma-rules";
export function useGetStreams(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["streams-index"],
    queryFn: () => defaultOnError(fetchStreams(), "Error fetching streams"),
    retry: 1,
    enabled: execute
  });
  return {
    loadingStreams: isLoading && !!execute,
    streams: (data == null ? void 0 : data.streams.sort((a, b) => {
      if (a.title > b.title) return 1;
      if (a.title < b.title) return -1;
      return 0;
    })) || [],
    streamCategories: (data == null ? void 0 : data.streams.reduce((acc, stream) => {
      var _a;
      (_a = stream.categories) == null ? void 0 : _a.forEach((category) => {
        if (!acc.includes(category)) acc.push(category);
      });
      return acc;
    }, []).sort((a, b) => defaultCompare(a, b))) || []
  };
}
export function useGetNotifications(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["notifications-index"],
    queryFn: () => defaultOnError(fetchNotifications(), "Error fetching notifications"),
    retry: 1,
    enabled: execute
  });
  return {
    loadingNotifications: isLoading && !!execute,
    notifications: (data == null ? void 0 : data.notifications.sort((a, b) => {
      if (a.title > b.title) return 1;
      if (a.title < b.title) return -1;
      return 0;
    })) || []
  };
}
export function useListGitRepos({ page, perPage, query = void 0, orderBy = void 0, direction = void 0 }, enabled = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["list-git-repos", page, perPage, query, orderBy, direction],
    queryFn: () => defaultOnError(listGitRepos(page, perPage, query, orderBy, direction), "Error fetching Git repositories"),
    retry: 1,
    enabled,
    placeholderData: keepPreviousData
  });
  return {
    loadingRepos: isLoading,
    repos: isLoading ? [] : data == null ? void 0 : data.sigma_git_repositories,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useListRepoContent(repoId, path, query) {
  const { data, isLoading } = useQuery({
    queryKey: ["list-git-repo-content", repoId, path, query],
    queryFn: () => defaultOnError(listRepoContent(repoId, path, query), "Error listing repository"),
    retry: 1,
    enabled: !!repoId
  });
  return {
    loadingRepoRules: isLoading && !!repoId,
    repoRules: data ? data.sort((a, b) => {
      if (a.type > b.type) return 1;
      if (a.type < b.type) return -1;
      return 0;
    }) : null
  };
}
export function useAddGitRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addGitRepo,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const error = data.success_count > 0 ? {
          type: "warning",
          message: `Repo added and imported ${data.success_count} rules successfully, ${data.failure_count} rules failed to import.`
        } : { type: "error", message: `Failed to add repo: ${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(`Repo added and all ${data.success_count} rules imported`);
      }
      queryClient.invalidateQueries({
        queryKey: ["list-git-repos"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addGitRepo: mutateAsync, addingRepo: isLoading };
}
export function useAddDefaultGitRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addDefaultRepo,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ["list-git-repos"]
    }),
    onError: (error) => UserNotification.error(error.message)
  });
  return { addDefaultRepo: mutateAsync, addingDefaultRepo: isLoading };
}
export function useUpdateGitRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateGitRepo,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const error = data.success_count > 0 ? {
          type: "warning",
          message: `Repo updated: updated ${data.success_count} rules successfully, ${data.failure_count} rules failed to update.`
        } : { type: "error", message: `Failed to update repo: ${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(`Repo updated with all ${data.success_count} rules.`);
      }
      queryClient.invalidateQueries({
        queryKey: ["list-git-repos"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { updateGitRepo: mutateAsync, updatingRepo: isLoading };
}
export function useImportAllRulesFromRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: importAllRulesFromRepo,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const warnMsg = `${data.success_count} rule${data.success_count === 1 ? "" : "s"} imported successfully. ${data.failure_count} rule${data.failure_count === 1 ? "" : "s"} failed to import. See server log for more detailed information.`;
        const error = data.success_count > 0 ? { type: "warning", message: warnMsg } : { type: "error", message: "All rules failed to import" };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(`All ${data.success_count} rules imported`);
      }
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    importRulesFromRepo: mutateAsync,
    importingRules: isLoading
  };
}
export function useRefreshRepoRules() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: refreshRepoRules,
    onSuccess: (data) => {
      if (data.failure_count > 0) {
        const warnMsg = `${data.success_count} rule${data.success_count === 1 ? "" : "s"} refreshed successfully. ${data.failure_count} rule${data.failure_count === 1 ? "" : "s"} failed to refresh. See server log for more detailed information.`;
        const error = data.success_count > 0 ? { type: "warning", message: warnMsg } : { type: "error", message: "All rules failed to refresh" };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(`All ${data.success_count} rules refreshed`);
      }
      queryClient.invalidateQueries({
        queryKey: [sigmaRulesKey]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    refreshRules: mutateAsync,
    refreshingRules: isLoading
  };
}
export function useDeleteSigmaRepo() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteSigmaRepo,
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: ["list-git-repos"]
    }),
    onError: (error) => UserNotification.error(error.message)
  });
  return { deleteSigmaRepo: mutateAsync, deletingRepo: isLoading };
}
export function useBulkDeleteSigmaRepos() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkSigmaRepoDelete,
    onSuccess: () => {
      UserNotification.success("Action completed");
      queryClient.invalidateQueries({
        queryKey: ["list-git-repos"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkDeleteSigmaRepos: mutateAsync, isMutating: isLoading };
}
export function useValidateSigmaRuleZip() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: validateSigmaRuleZip,
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    validateSigmaRuleZip: mutateAsync,
    isValidating: isLoading
  };
}
const pluralSuffix = (n) => n === 1 ? "" : "s";
const ruleDisplayName = (r) => {
  var _a, _b;
  return (_b = (_a = r.event_definition_name) != null ? _a : r.source_name) != null ? _b : "unknown rule";
};
function notifyImportResult(data) {
  var _a;
  const results = (_a = data.results) != null ? _a : [];
  const hasResults = results.length > 0;
  const created = hasResults ? results.filter((r) => r.status === "created") : [];
  const skipped = hasResults ? results.filter((r) => r.status === "skipped") : [];
  const failed = hasResults ? results.filter((r) => r.status === "failed") : [];
  if (failed.length > 0) {
    const message = failed.map((r) => {
      var _a2;
      return `${ruleDisplayName(r)}: ${(_a2 = r.error) != null ? _a2 : "Unknown error"}`;
    }).join(", ");
    UserNotification.error(`Failed to import ${failed.length} rule${pluralSuffix(failed.length)}: ${message}`);
  } else if (skipped.length > 0) {
    const importedCount = hasResults ? created.length : data.success_count;
    const names = skipped.map(ruleDisplayName).join(", ");
    UserNotification.warning(
      `${importedCount} rule${pluralSuffix(importedCount)} imported. ${skipped.length} skipped (already exist): ${names}`
    );
  } else {
    const importedCount = hasResults ? created.length : data.success_count;
    UserNotification.success(`${importedCount} rule${pluralSuffix(importedCount)} imported successfully`);
  }
}
export function useValidateSigmaRuleYaml() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: validateSigmaRuleYaml,
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    validateSigmaRuleYaml: mutateAsync,
    isValidating: isLoading
  };
}
export function useBulkImportFromRepo() {
  const { mutateAsync, isPending: isImporting } = useMutation({
    mutationFn: bulkImportFromRepo,
    onSuccess: notifyImportResult,
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkImportFromRepo: mutateAsync, isImporting };
}
export function useBulkUploadEventDefs() {
  const { mutateAsync, isPending: isUploading } = useMutation({
    mutationFn: bulkUploadEventDefs,
    onSuccess: notifyImportResult,
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkUploadEventDefs: mutateAsync, isUploading };
}
